'use client';

import { useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { isDarkTheme } from '@/lib/helpers';
import { DashWidget, Plant } from '@/lib/types';
import ApexChartWrapper from '../ApexChartWrapper';

interface Props { widget: DashWidget; plant: Plant; }

/*
 * Operating Room Monitor — environment of a hospital's operating theatres, one
 * Dixel iPro controller per room read over Modbus RTU. Four tabs:
 *   • Overview — room cards: temp / RH / room pressure / HEPA ΔP / ACH, door,
 *                AHU, and today's case schedule with a now marker
 *   • Trend    — 24 h trend per room plus time-in-range for every parameter
 *   • Alarms   — active alarms (acknowledgeable), 24 h alarm log, thresholds
 *   • Devices  — the three PLCs and the Modbus point list
 *
 * DEMO DATA. The three rooms are hardcoded and every value is generated
 * in-widget, seeded by wall-clock time, so a reload shows the same 24 hours and
 * the "now" values match the tail of the trend. OR-3 is written as the problem
 * room: a loading HEPA filter starves airflow in the afternoon, so pressure and
 * ACH sag while RH climbs — the alarm tabs have something real-looking to show.
 * Labels, Modbus addresses and limits come from widget.orMonitor, so wiring the
 * real Dixel registers replaces sample() without touching the layout.
 *
 * Limits default to ASHRAE 170-2021 for an operating room and are flagged on
 * screen as needing confirmation by the hospital's infection-control committee.
 */

type Tab = 'overview' | 'trend' | 'alarm' | 'device';
type Param = 'temp' | 'rh' | 'dp' | 'hepa' | 'ach';
type State = 'ok' | 'warn' | 'bad';

const S = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#7b5fd6', '#0e97ad'];
const OK = '#1baf7a', WARN = '#eda100', BAD = '#e34948';
const TXT = 'var(--text-primary)';
const MUT = 'color-mix(in srgb, var(--text-muted) 60%, var(--text-primary))';
const BD = 'color-mix(in srgb, var(--text-muted) 34%, transparent)';
const tnum = { fontVariantNumeric: 'tabular-nums' as const };
const STATE_C: Record<State, string> = { ok: OK, warn: WARN, bad: BAD };

const DAY_MS = 86_400_000;
const BKK_MS = 7 * 3_600_000;
const STEP_MS = 5 * 60_000;
const HIST = 288;          // 24 h of 5-minute samples
const CLEAN_MIN = 30;      // turnover cleaning after each case
const TL_FROM = 7 * 60, TL_TO = 20 * 60;

interface Limits { tempMin: number; tempMax: number; rhMin: number; rhMax: number; dpMin: number; hepaMax: number; achMin: number }

// ASHRAE 170-2021 Table 7-1, operating room: 20–24 °C, RH ≤ 60 %, positive to
// adjacent spaces, ≥ 20 total ACH. The 2.5 Pa floor is the standard's minimum
// pressure differential; the HEPA figure is a typical final resistance.
const DEFAULT_LIMITS: Limits = { tempMin: 20, tempMax: 24, rhMin: 20, rhMax: 60, dpMin: 2.5, hepaMax: 450, achMin: 20 };

interface Case { from: string; to: string; th: string; en: string }
interface RoomDef {
  label: string; device: string; model: string; addr: number; fw: string;
  t: number; tA: number; rh: number; rhA: number; dp: number; dpA: number; hepa: number; ach: number; achA: number;
  pmDrift: number;
  cases: Case[];
}

const ROOMS: RoomDef[] = [
  {
    label: 'OR-1', device: 'DXL-OR01', model: 'Dixel iPro IPG108D', addr: 1, fw: '2.4.1',
    t: 21.2, tA: 0.9, rh: 50, rhA: 7, dp: 12, dpA: 2.5, hepa: 185, ach: 25, achA: 2, pmDrift: 0,
    cases: [
      { from: '08:00', to: '10:30', th: 'ผ่าตัดถุงน้ำดีผ่านกล้อง', en: 'Laparoscopic cholecystectomy' },
      { from: '11:15', to: '13:45', th: 'ผ่าตัดเปลี่ยนข้อเข่า', en: 'Total knee arthroplasty' },
      { from: '14:30', to: '16:00', th: 'ผ่าตัดไส้เลื่อน', en: 'Hernia repair' },
      { from: '18:00', to: '19:15', th: 'ผ่าตัดไส้ติ่ง (ฉุกเฉิน)', en: 'Appendectomy (emergency)' },
    ],
  },
  {
    label: 'OR-2', device: 'DXL-OR02', model: 'Dixel iPro IPG108D', addr: 2, fw: '2.4.1',
    t: 22.0, tA: 0.9, rh: 53, rhA: 7, dp: 10, dpA: 2.5, hepa: 215, ach: 23.5, achA: 2, pmDrift: 0,
    cases: [
      { from: '09:00', to: '10:00', th: 'ผ่าตัดคลอด', en: 'Cesarean section' },
      { from: '13:00', to: '15:30', th: 'ผ่าตัดกระดูกสันหลัง', en: 'Spinal fusion' },
    ],
  },
  {
    label: 'OR-3', device: 'DXL-OR03', model: 'Dixel iPro IPG108D', addr: 3, fw: '2.3.7',
    t: 22.8, tA: 1.2, rh: 55, rhA: 6, dp: 4.8, dpA: 2.8, hepa: 368, ach: 22, achA: 2.2, pmDrift: 1,
    cases: [
      { from: '08:30', to: '12:00', th: 'ผ่าตัดต่อมไทรอยด์', en: 'Thyroidectomy' },
      { from: '13:00', to: '17:00', th: 'ผ่าตัดเปลี่ยนข้อสะโพก', en: 'Total hip arthroplasty' },
    ],
  },
];

// Modbus point list. Addresses are placeholders until the PLC program is read.
const POINTS = [
  { reg: '30001', type: 'IR', th: 'อุณหภูมิห้อง', en: 'Room temperature', scale: '×0.1', unit: '°C' },
  { reg: '30002', type: 'IR', th: 'ความชื้นสัมพัทธ์', en: 'Relative humidity', scale: '×0.1', unit: '%RH' },
  { reg: '30003', type: 'IR', th: 'ความดันห้องเทียบทางเดิน', en: 'Room ΔP vs corridor', scale: '×0.1', unit: 'Pa' },
  { reg: '30004', type: 'IR', th: 'ความดันตกคร่อม HEPA', en: 'HEPA filter ΔP', scale: '×1', unit: 'Pa' },
  { reg: '30005', type: 'IR', th: 'อัตราการเปลี่ยนอากาศ', en: 'Air changes', scale: '×0.1', unit: 'ACH' },
  { reg: '10001', type: 'DI', th: 'สถานะประตู', en: 'Door contact', scale: '0/1', unit: 'open' },
  { reg: '10002', type: 'DI', th: 'AHU ทำงาน', en: 'AHU run', scale: '0/1', unit: 'run' },
  { reg: '10003', type: 'DI', th: 'AHU ฟอลต์', en: 'AHU fault', scale: '0/1', unit: 'fault' },
];

function seeded(n: number): number {
  const x = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const toMin = (hhmm: string) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
const minOfDay = (t: number) => ((((t + BKK_MS) % DAY_MS) + DAY_MS) % DAY_MS) / 60_000;
const hhmm = (t: number) => {
  const m = Math.floor(minOfDay(t));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};
const dur = (mins: number) => `${Math.floor(mins / 60)}:${String(Math.floor(mins % 60)).padStart(2, '0')}`;

// Smooth value noise in [-0.5, 0.5] with knots every periodMin minutes.
function smooth(seed: number, t: number, periodMin: number): number {
  const P = periodMin * 60_000;
  const k = Math.floor(t / P);
  let f = (t - k * P) / P;
  f = f * f * (3 - 2 * f);
  const a = seeded(seed * 100_003 + k), b = seeded(seed * 100_003 + k + 1);
  return a + (b - a) * f - 0.5;
}

interface RoomStatus { kind: 'case' | 'clean' | 'idle'; c?: Case; mins?: number; next?: Case }
function roomStatus(r: RoomDef, t: number): RoomStatus {
  const m = minOfDay(t);
  for (const c of r.cases) {
    const a = toMin(c.from), b = toMin(c.to);
    if (m >= a && m < b) return { kind: 'case', c, mins: m - a };
    if (m >= b && m < b + CLEAN_MIN) return { kind: 'clean', c, mins: m - b };
  }
  return { kind: 'idle', next: r.cases.find((c) => toMin(c.from) > m) };
}

// Doors swing more during turnover than mid-case; each opening lasts ~20 s.
function doorOpen(ri: number, t: number, kind: RoomStatus['kind']): boolean {
  const p = kind === 'case' ? 0.05 : kind === 'clean' ? 0.4 : 0.12;
  return seeded(ri * 7717 + Math.floor(t / 60_000)) < p && t % 60_000 < 20_000;
}

type Sample = Record<Param, number>;
function sample(ri: number, r: RoomDef, t: number): Sample {
  const h = minOfDay(t) / 60;
  const pm = r.pmDrift * Math.exp(-((h - 15) ** 2) / 8);
  const inCase = roomStatus(r, t).kind === 'case';
  const jit = (s: number) => seeded(ri * 31 + s * 7 + Math.floor(t / 5000)) - 0.5;
  return {
    temp: r.t + 0.35 * Math.sin((2 * Math.PI * (h - 9)) / 24) + r.tA * smooth(ri * 10 + 1, t, 20) - (inCase ? 0.3 : 0) + pm * 0.5 + jit(1) * 0.04,
    rh: r.rh + 2.5 * Math.sin((2 * Math.PI * (h - 8)) / 24) + r.rhA * smooth(ri * 10 + 2, t, 30) + pm * 3.5 + jit(2) * 0.3,
    dp: Math.max(0.2, r.dp + r.dpA * smooth(ri * 10 + 3, t, 10) - pm * 1.6 + jit(3) * 0.15),
    hepa: r.hepa + 6 * smooth(ri * 10 + 4, t, 90) + pm * 8,
    ach: r.ach + r.achA * smooth(ri * 10 + 5, t, 15) - pm * 1.4 + jit(5) * 0.1,
  };
}

function paramState(p: Param, v: number, L: Limits): State {
  switch (p) {
    case 'temp': return v < L.tempMin || v > L.tempMax ? 'bad' : v < L.tempMin + 0.5 || v > L.tempMax - 0.5 ? 'warn' : 'ok';
    case 'rh': return v < L.rhMin || v > L.rhMax ? 'bad' : v < L.rhMin + 3 || v > L.rhMax - 3 ? 'warn' : 'ok';
    case 'dp': return v < L.dpMin ? 'bad' : v < L.dpMin * 2 ? 'warn' : 'ok';
    case 'hepa': return v > L.hepaMax ? 'bad' : v > L.hepaMax * 0.78 ? 'warn' : 'ok';
    case 'ach': return v < L.achMin ? 'bad' : v < L.achMin * 1.1 ? 'warn' : 'ok';
  }
}
// Larger = further from healthy; picks the peak value of an alarm episode.
function severity(p: Param, v: number, L: Limits): number {
  switch (p) {
    case 'temp': return Math.abs(v - (L.tempMin + L.tempMax) / 2);
    case 'rh': return Math.abs(v - (L.rhMin + L.rhMax) / 2);
    case 'dp': return -v;
    case 'hepa': return v;
    case 'ach': return -v;
  }
}
const worst = (states: State[]): State => (states.includes('bad') ? 'bad' : states.includes('warn') ? 'warn' : 'ok');

const PARAMS: Array<{ key: Param; th: string; en: string; short: string; unit: string; dig: number; range: (L: Limits) => string }> = [
  { key: 'temp', th: 'อุณหภูมิ', en: 'Temperature', short: 'Temp', unit: '°C', dig: 1, range: (L) => `${L.tempMin}–${L.tempMax} °C` },
  { key: 'rh', th: 'ความชื้น', en: 'Humidity', short: 'RH', unit: '%', dig: 0, range: (L) => `${L.rhMin}–${L.rhMax} %` },
  { key: 'dp', th: 'ความดันห้อง', en: 'Room pressure', short: 'ΔP', unit: 'Pa', dig: 1, range: (L) => `≥ +${L.dpMin} Pa` },
  { key: 'hepa', th: 'HEPA ΔP', en: 'HEPA ΔP', short: 'HEPA', unit: 'Pa', dig: 0, range: (L) => `≤ ${L.hepaMax} Pa` },
  { key: 'ach', th: 'เปลี่ยนอากาศ', en: 'Air changes', short: 'ACH', unit: '/h', dig: 1, range: (L) => `≥ ${L.achMin} /h` },
];
const PMETA = Object.fromEntries(PARAMS.map((p) => [p.key, p])) as Record<Param, (typeof PARAMS)[number]>;

export default function OrMonitorWidget({ widget }: Props) {
  const th = useAppStore((s) => s.lang) === 'th';
  const dark = isDarkTheme();
  const [tab, setTab] = useState<Tab>('overview');
  const [sel, setSel] = useState(0);
  const [acked, setAcked] = useState<Record<string, boolean>>({});
  const [clock, setClock] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setClock(Date.now()), 5000);
    return () => clearInterval(id);
  }, []);

  const cfg = widget.orMonitor;
  const rooms = useMemo<RoomDef[]>(
    () => ROOMS.map((r, i) => ({ ...r, ...(cfg?.rooms?.[i] ?? {}) })),
    [cfg?.rooms],
  );
  const L = useMemo<Limits>(() => ({ ...DEFAULT_LIMITS, ...(cfg?.limits ?? {}) }), [cfg?.limits]);

  // History only moves once a minute; rebuild it then, not on every 5 s tick.
  const minute = Math.floor(clock / 60_000);
  const hist = useMemo(() => {
    const end = Math.floor((minute * 60_000) / STEP_MS) * STEP_MS;
    const times = Array.from({ length: HIST + 1 }, (_, i) => end - (HIST - i) * STEP_MS);
    const data = rooms.map((r, ri) => times.map((t) => sample(ri, r, t)));

    const events: Array<{ ri: number; p: Param; start: number; end?: number; peak: number }> = [];
    const compliance = data.map((rows) => {
      const inRange = Object.fromEntries(PARAMS.map((p) => [p.key, 0])) as Record<Param, number>;
      let all = 0;
      rows.forEach((s) => {
        let good = true;
        PARAMS.forEach((p) => { if (paramState(p.key, s[p.key], L) !== 'bad') inRange[p.key]++; else good = false; });
        if (good) all++;
      });
      const pct = (n: number) => (n / rows.length) * 100;
      return { ...(Object.fromEntries(PARAMS.map((p) => [p.key, pct(inRange[p.key])])) as Record<Param, number>), all: pct(all) };
    });
    data.forEach((rows, ri) => PARAMS.forEach(({ key }) => {
      let open: (typeof events)[number] | null = null;
      rows.forEach((s, i) => {
        const bad = paramState(key, s[key], L) === 'bad';
        if (bad && !open) { open = { ri, p: key, start: times[i], peak: s[key] }; events.push(open); }
        else if (bad && open) { if (severity(key, s[key], L) > severity(key, open.peak, L)) open.peak = s[key]; }
        else if (!bad && open) { open.end = times[i]; open = null; }
      });
    }));
    events.sort((a, b) => b.start - a.start);
    return { times, data, events, compliance };
  }, [minute, rooms, L]);

  const now = rooms.map((r, ri) => {
    const v = sample(ri, r, clock);
    const status = roomStatus(r, clock);
    const door = doorOpen(ri, clock, status.kind);
    const st = Object.fromEntries(PARAMS.map((p) => [p.key, paramState(p.key, v[p.key], L)])) as Record<Param, State>;
    // A pressure dip with the door open is expected, not an alarm.
    const dpShown = door ? 0.3 + seeded(ri + Math.floor(clock / 5000)) * 0.6 : v.dp;
    if (door) st.dp = 'ok';
    const ahuFault = false;
    const age = 1 + Math.floor(seeded(ri * 13 + Math.floor(clock / 5000)) * 4);
    return { r, ri, v: { ...v, dp: dpShown }, st, status, door, ahuFault, age, overall: worst(Object.values(st)) };
  });
  const active = now.flatMap((n) => PARAMS.filter((p) => n.st[p.key] !== 'ok').map((p) => ({
    key: `${n.ri}-${p.key}`, n, p, state: n.st[p.key], value: n.v[p.key],
  }))).sort((a, b) => (a.state === b.state ? 0 : a.state === 'bad' ? -1 : 1));
  const activeBad = active.filter((a) => a.state === 'bad');

  const f = (v: number, d: number) => v.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
  const fmtP = (p: Param, v: number) => `${p === 'dp' ? '+' : ''}${f(v, PMETA[p].dig)}`;

  const axisCol = dark ? '#94a3b8' : '#64748b';
  const gridCol = dark ? 'rgba(148,163,184,0.14)' : 'rgba(100,116,139,0.14)';
  const baseChart = (): ApexCharts.ApexOptions => ({
    chart: { type: 'line', toolbar: { show: false }, animations: { enabled: false }, background: 'transparent', fontFamily: 'inherit', zoom: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { width: 2.2, curve: 'smooth' },
    grid: { borderColor: gridCol, strokeDashArray: 3, xaxis: { lines: { show: false } }, padding: { left: 4, right: 8, top: 0, bottom: 0 } },
    legend: { show: true, position: 'top', horizontalAlign: 'left', fontSize: '11px', labels: { colors: axisCol }, markers: { size: 5 } },
    tooltip: { theme: dark ? 'dark' : 'light', shared: true, intersect: false },
    xaxis: { categories: hist.times.map(hhmm), tickAmount: 12, labels: { style: { colors: axisCol, fontSize: '10px' }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false } },
  });
  const yLab = (fmt: (v: number) => string) => ({ style: { colors: axisCol, fontSize: '10px' }, formatter: fmt });

  // ── shared pieces ──────────────────────────────────────────────────────────
  const card = (accent?: string): React.CSSProperties => ({
    background: accent ? `linear-gradient(180deg, color-mix(in srgb, ${accent} 5%, var(--bg-surface)), var(--bg-surface) 70%)` : 'var(--bg-surface)',
    border: `1px solid ${accent ? `color-mix(in srgb, ${accent} 24%, var(--glass-border))` : 'var(--glass-border)'}`,
    borderRadius: 14, padding: '10px 13px',
    boxShadow: '0 1px 2px rgba(16,24,40,.045), 0 10px 22px -18px rgba(16,24,40,.4)',
  });
  const Kpi = (o: { label: string; value: string; unit?: string; sub?: string; accent: string; state?: State }) => (
    <div style={card(o.accent)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: o.state ? STATE_C[o.state] : o.accent, flexShrink: 0 }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: MUT, textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 5 }}>
        <span style={{ fontSize: 21, fontWeight: 800, color: TXT, letterSpacing: '-0.02em', ...tnum }}>{o.value}</span>
        {o.unit && <span style={{ fontSize: 10.5, color: MUT, fontWeight: 600 }}>{o.unit}</span>}
      </div>
      {o.sub && <div style={{ fontSize: 10.5, color: MUT, marginTop: 3, ...tnum }}>{o.sub}</div>}
    </div>
  );
  const Panel = (o: { title: string; note?: string; children: React.ReactNode; grow?: string; minW?: number }) => (
    <div style={{ ...card(), flex: o.grow || '1 1 320px', minWidth: o.minW ?? 260, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 10.5, fontWeight: 800, color: MUT, textTransform: 'uppercase', letterSpacing: '.06em' }}>{o.title}</span>
        {o.note && <span style={{ fontSize: 10, color: MUT, opacity: 0.8, marginLeft: 'auto' }}>{o.note}</span>}
      </div>
      {o.children}
    </div>
  );
  const row = (children: React.ReactNode) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'stretch' }}>{children}</div>
  );
  const chip = (state: State | 'idle' | 'info', text: string) => {
    const c = state === 'idle' ? 'var(--text-muted)' : state === 'info' ? S[0] : STATE_C[state];
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, color: c, padding: '2px 8px', borderRadius: 99, background: `color-mix(in srgb, ${c} 12%, transparent)`, whiteSpace: 'nowrap' }}>
        <span style={{ width: 6, height: 6, borderRadius: 99, background: c }} />{text}
      </span>
    );
  };
  const thS = { padding: '7px 8px', fontSize: 10, fontWeight: 800, color: MUT, textTransform: 'uppercase' as const, letterSpacing: '.04em', textAlign: 'left' as const, whiteSpace: 'nowrap' as const };
  const tdS = { padding: '7px 8px', fontSize: 12, color: TXT, textAlign: 'left' as const, borderTop: `1px solid ${BD}`, whiteSpace: 'nowrap' as const, ...tnum };
  const statusChip = (s: RoomStatus) =>
    s.kind === 'case' ? chip('info', `${th ? 'กำลังผ่าตัด' : 'In surgery'} ${dur(s.mins ?? 0)}`)
      : s.kind === 'clean' ? chip('warn', th ? 'ทำความสะอาด' : 'Turnover clean')
        : chip('idle', s.next ? `${th ? 'ว่าง · เคสถัดไป' : 'Idle · next'} ${s.next.from}` : (th ? 'ว่าง' : 'Idle'));
  const roomName = (r: RoomDef) => `${r.label}${th ? ' ห้องผ่าตัด' : ''}`;

  // ── Overview ───────────────────────────────────────────────────────────────
  const overviewTab = () => {
    const nowMin = minOfDay(clock);
    const casesAll = rooms.flatMap((r) => r.cases);
    const casesDone = casesAll.filter((c) => toMin(c.to) <= nowMin).length;
    const inSurgery = now.filter((n) => n.status.kind === 'case').length;
    const okRooms = now.filter((n) => n.overall !== 'bad').length;
    const comp24 = hist.compliance.reduce((a, c) => a + c.all, 0) / hist.compliance.length;

    const tile = (n: (typeof now)[number], p: Param) => {
      const c = STATE_C[n.st[p]];
      const doorNote = p === 'dp' && n.door;
      return (
        <div key={p} style={{ flex: '1 1 0', minWidth: 0, padding: '7px 9px', borderRadius: 10, background: `color-mix(in srgb, ${c} 8%, transparent)`, border: `1px solid color-mix(in srgb, ${c} 28%, transparent)` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: MUT, textTransform: 'uppercase', letterSpacing: '.04em' }}>{th ? PMETA[p].th : PMETA[p].en}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, marginTop: 2 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: n.st[p] === 'ok' ? TXT : c, letterSpacing: '-0.02em', ...tnum }}>{fmtP(p, n.v[p])}</span>
            <span style={{ fontSize: 10.5, color: MUT, fontWeight: 600 }}>{PMETA[p].unit}</span>
          </div>
          <div style={{ fontSize: 10, color: doorNote ? WARN : MUT, ...tnum }}>{doorNote ? (th ? 'ประตูเปิด' : 'door open') : PMETA[p].range(L)}</div>
        </div>
      );
    };

    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {Kpi({ label: th ? 'ห้องอยู่ในเกณฑ์' : 'Rooms in spec', value: `${okRooms}`, unit: `/ ${rooms.length}`, accent: S[2], state: okRooms === rooms.length ? 'ok' : 'bad', sub: th ? 'ตามเกณฑ์ ASHRAE 170' : 'against ASHRAE 170' })}
          {Kpi({ label: th ? 'แจ้งเตือนขณะนี้' : 'Active alarms', value: `${activeBad.length}`, accent: S[1], state: activeBad.length ? 'bad' : 'ok', sub: `${active.length - activeBad.length} ${th ? 'เฝ้าระวัง' : 'warnings'}` })}
          {Kpi({ label: th ? 'ห้องที่กำลังผ่าตัด' : 'Rooms in surgery', value: `${inSurgery}`, unit: `/ ${rooms.length}`, accent: S[0], sub: `${th ? 'เคสวันนี้' : 'Cases today'} ${casesDone}/${casesAll.length} ${th ? 'เสร็จแล้ว' : 'done'}` })}
          {Kpi({ label: th ? 'อยู่ในเกณฑ์ 24 ชม.' : 'Time in spec · 24 h', value: f(comp24, 1), unit: '%', accent: S[5], state: comp24 >= 95 ? 'ok' : comp24 >= 85 ? 'warn' : 'bad', sub: th ? 'เฉลี่ยทุกห้อง ทุกพารามิเตอร์' : 'all rooms, all parameters' })}
          {Kpi({ label: th ? 'Dixel PLC ออนไลน์' : 'Dixel PLCs online', value: `${rooms.length}`, unit: `/ ${rooms.length}`, accent: S[6], state: 'ok', sub: 'Modbus RTU · poll 5 s' })}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 10 }}>
          {now.map((n) => {
            const accent = STATE_C[n.overall];
            const nowPct = ((nowMin - TL_FROM) / (TL_TO - TL_FROM)) * 100;
            return (
              <div key={n.r.label} style={{ ...card(accent), display: 'flex', flexDirection: 'column', gap: 8, borderWidth: n.overall === 'ok' ? 1 : 2 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 99, background: accent, boxShadow: `0 0 0 4px color-mix(in srgb, ${accent} 18%, transparent)` }} />
                  <span style={{ fontSize: 15, fontWeight: 800, color: TXT }}>{roomName(n.r)}</span>
                  <span style={{ marginLeft: 'auto' }}>{statusChip(n.status)}</span>
                </div>
                <div style={{ fontSize: 11.5, color: MUT, minHeight: 16, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {n.status.c ? `${th ? n.status.c.th : n.status.c.en} · ${n.status.c.from}–${n.status.c.to}` : n.status.next ? `${th ? 'ถัดไป' : 'Next'}: ${th ? n.status.next.th : n.status.next.en}` : (th ? 'ไม่มีเคสเหลือวันนี้' : 'No more cases today')}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>{(['temp', 'rh', 'dp'] as Param[]).map((p) => tile(n, p))}</div>
                <div style={{ display: 'flex', gap: 6 }}>{(['hepa', 'ach'] as Param[]).map((p) => tile(n, p))}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {chip(n.door ? 'warn' : 'ok', n.door ? (th ? 'ประตูเปิด' : 'Door open') : (th ? 'ประตูปิด' : 'Door closed'))}
                  {chip(n.ahuFault ? 'bad' : 'ok', th ? 'AHU ทำงาน' : 'AHU running')}
                  {n.st.hepa !== 'ok' && chip(n.st.hepa, th ? 'ควรเปลี่ยนแผ่นกรอง' : 'Filter change due')}
                </div>
                <div>
                  <div style={{ position: 'relative', height: 14, borderRadius: 4, background: `color-mix(in srgb, var(--text-muted) 12%, transparent)`, overflow: 'hidden' }}>
                    {n.r.cases.map((c) => {
                      const a = ((toMin(c.from) - TL_FROM) / (TL_TO - TL_FROM)) * 100;
                      const b = ((toMin(c.to) - TL_FROM) / (TL_TO - TL_FROM)) * 100;
                      return <div key={c.from} title={`${c.from}–${c.to} ${th ? c.th : c.en}`} style={{ position: 'absolute', top: 0, bottom: 0, left: `${clamp(a, 0, 100)}%`, width: `${clamp(b, 0, 100) - clamp(a, 0, 100)}%`, background: `color-mix(in srgb, ${S[0]} ${toMin(c.to) <= nowMin ? 35 : 70}%, transparent)`, borderRadius: 3 }} />;
                    })}
                    {nowPct >= 0 && nowPct <= 100 && <div style={{ position: 'absolute', top: -2, bottom: -2, left: `${nowPct}%`, width: 2, background: BAD }} />}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9.5, color: MUT, marginTop: 2, ...tnum }}>
                    <span>07:00</span><span>10:00</span><span>13:00</span><span>16:00</span><span>20:00</span>
                  </div>
                </div>
                <div style={{ fontSize: 10.5, color: MUT, borderTop: `1px solid ${BD}`, paddingTop: 6, ...tnum }}>
                  {n.r.model} · {n.r.device} · addr {n.r.addr} · {th ? `อัปเดต ${n.age} วินาทีก่อน` : `updated ${n.age}s ago`}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // ── Trend ──────────────────────────────────────────────────────────────────
  const trendTab = () => {
    const rows = hist.data[sel];
    const round = (d: number) => (s: Sample, k: Param) => +s[k].toFixed(d);
    const r1 = round(1), r0 = round(0);

    const climate: ApexCharts.ApexOptions = {
      ...baseChart(),
      colors: [S[1], S[6]],
      yaxis: [
        { min: 18, max: 26, tickAmount: 4, labels: yLab((v) => `${f(v, 0)}°C`) },
        { opposite: true, min: 30, max: 70, tickAmount: 4, labels: yLab((v) => `${Math.round(v)}%`) },
      ],
      annotations: {
        yaxis: [
          { y: L.tempMin, y2: L.tempMax, yAxisIndex: 0, fillColor: S[1], opacity: 0.06, borderColor: 'transparent', label: { text: `${L.tempMin}–${L.tempMax} °C`, position: 'left', textAnchor: 'start', style: { background: S[1], color: '#fff', fontSize: '10px' } } },
          { y: L.rhMax, yAxisIndex: 1, borderColor: S[6], strokeDashArray: 5, label: { text: `RH ≤ ${L.rhMax}%`, style: { background: S[6], color: '#fff', fontSize: '10px' } } },
        ],
      },
      tooltip: { ...baseChart().tooltip, y: { formatter: (v: number, o?: { seriesIndex: number }) => (o?.seriesIndex === 0 ? `${f(v, 1)} °C` : `${f(v, 1)} %RH`) } },
    };
    const airflow: ApexCharts.ApexOptions = {
      ...baseChart(),
      colors: [S[0], S[5]],
      yaxis: [
        { min: 0, max: 16, tickAmount: 4, labels: yLab((v) => `${Math.round(v)} Pa`) },
        { opposite: true, min: 10, max: 30, tickAmount: 4, labels: yLab((v) => `${Math.round(v)}`) },
      ],
      annotations: {
        yaxis: [
          { y: L.dpMin, yAxisIndex: 0, borderColor: BAD, strokeDashArray: 5, label: { text: `ΔP ≥ ${L.dpMin} Pa`, position: 'left', textAnchor: 'start', style: { background: BAD, color: '#fff', fontSize: '10px' } } },
          { y: L.achMin, yAxisIndex: 1, borderColor: S[5], strokeDashArray: 5, label: { text: `ACH ≥ ${L.achMin}`, style: { background: S[5], color: '#fff', fontSize: '10px' } } },
        ],
      },
      tooltip: { ...baseChart().tooltip, y: { formatter: (v: number, o?: { seriesIndex: number }) => (o?.seriesIndex === 0 ? `+${f(v, 1)} Pa` : `${f(v, 1)} ACH`) } },
    };

    const pctCell = (v: number, key?: string) => {
      const c = v >= 98 ? OK : v >= 90 ? WARN : BAD;
      return <td key={key} style={{ ...tdS, textAlign: 'right', color: c, fontWeight: 700 }}>{f(v, 1)}%</td>;
    };

    return (
      <>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {rooms.map((r, i) => (
            <button key={r.label} onClick={() => setSel(i)}
              style={{ padding: '5px 12px', fontSize: 12, fontWeight: 700, borderRadius: 99, cursor: 'pointer', border: `1px solid ${sel === i ? S[0] : BD}`, background: sel === i ? `color-mix(in srgb, ${S[0]} 14%, transparent)` : 'transparent', color: sel === i ? S[0] : MUT }}>
              <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 99, background: STATE_C[now[i].overall], marginRight: 6 }} />{r.label}
            </button>
          ))}
          <span style={{ fontSize: 10.5, color: MUT, marginLeft: 'auto' }}>{th ? '24 ชม. · ทุก 5 นาที' : '24 h · 5-minute samples'}</span>
        </div>
        {row(<>
          {Panel({
            title: th ? `อุณหภูมิ & ความชื้น · ${rooms[sel].label}` : `Temperature & humidity · ${rooms[sel].label}`, grow: '1 1 420px',
            children: <div style={{ height: 240 }}><ApexChartWrapper options={climate} type="line" height="100%" series={[
              { name: th ? 'อุณหภูมิ (°C)' : 'Temp (°C)', data: rows.map((s) => r1(s, 'temp')) },
              { name: th ? 'ความชื้น (%RH)' : 'Humidity (%RH)', data: rows.map((s) => r0(s, 'rh')) },
            ]} /></div>,
          })}
          {Panel({
            title: th ? `ความดันห้อง & การเปลี่ยนอากาศ · ${rooms[sel].label}` : `Room pressure & air changes · ${rooms[sel].label}`, grow: '1 1 420px',
            children: <div style={{ height: 240 }}><ApexChartWrapper options={airflow} type="line" height="100%" series={[
              { name: th ? 'ความดันห้อง (Pa)' : 'Room ΔP (Pa)', data: rows.map((s) => r1(s, 'dp')) },
              { name: 'ACH', data: rows.map((s) => r1(s, 'ach')) },
            ]} /></div>,
          })}
        </>)}
        {Panel({
          title: th ? 'สัดส่วนเวลาที่อยู่ในเกณฑ์ · 24 ชม.' : 'Time within limits · 24 h',
          note: th ? 'ใช้ประกอบรายงาน IC / HA' : 'for IC / accreditation reporting',
          children: (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead><tr>
                  <th style={thS}>{th ? 'ห้อง' : 'Room'}</th>
                  {PARAMS.map((p) => <th key={p.key} style={{ ...thS, textAlign: 'right' }}>{p.short}</th>)}
                  <th style={{ ...thS, textAlign: 'right', color: TXT }}>{th ? 'ทุกค่า' : 'All'}</th>
                  <th style={{ ...thS, textAlign: 'right' }}>{th ? 'ครั้งที่เกิน' : 'Excursions'}</th>
                </tr></thead>
                <tbody>
                  {rooms.map((r, i) => (
                    <tr key={r.label}>
                      <td style={{ ...tdS, fontWeight: 700 }}>{r.label}</td>
                      {PARAMS.map((p) => pctCell(hist.compliance[i][p.key], p.key))}
                      {pctCell(hist.compliance[i].all)}
                      <td style={{ ...tdS, textAlign: 'right' }}>{hist.events.filter((e) => e.ri === i).length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ),
        })}
      </>
    );
  };

  // ── Alarms ─────────────────────────────────────────────────────────────────
  const alarmTab = () => (
    <>
      {row(<>
        {Panel({
          title: th ? 'แจ้งเตือนขณะนี้' : 'Active alarms', note: `${activeBad.length} ${th ? 'วิกฤต' : 'critical'} · ${active.length - activeBad.length} ${th ? 'เฝ้าระวัง' : 'warning'}`, grow: '1 1 380px',
          children: active.length === 0
            ? <div style={{ padding: '18px 0', textAlign: 'center' }}>{chip('ok', th ? 'ทุกห้องอยู่ในเกณฑ์' : 'All rooms within limits')}</div>
            : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {active.map((a) => {
                  const c = STATE_C[a.state];
                  const isAck = acked[a.key];
                  return (
                    <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 10, borderLeft: `4px solid ${c}`, background: `color-mix(in srgb, ${c} ${isAck ? 4 : 9}%, transparent)`, opacity: isAck ? 0.7 : 1 }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: TXT, width: 42 }}>{a.n.r.label}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: TXT }}>
                          {th ? a.p.th : a.p.en} <span style={{ color: c, ...tnum }}>{fmtP(a.p.key, a.value)} {a.p.unit}</span>
                        </div>
                        <div style={{ fontSize: 10.5, color: MUT }}>{th ? 'เกณฑ์' : 'Limit'} {a.p.range(L)} · {a.state === 'bad' ? (th ? 'เกินเกณฑ์' : 'out of limit') : (th ? 'ใกล้เกณฑ์' : 'near limit')}</div>
                      </div>
                      {a.state === 'bad' && (
                        <button onClick={() => setAcked((s) => ({ ...s, [a.key]: !s[a.key] }))}
                          style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, cursor: 'pointer', border: `1px solid ${isAck ? BD : c}`, background: 'transparent', color: isAck ? MUT : c }}>
                          {isAck ? (th ? 'รับทราบแล้ว' : 'Acknowledged') : (th ? 'รับทราบ' : 'Acknowledge')}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ),
        })}
        {Panel({
          title: th ? 'เกณฑ์การแจ้งเตือน' : 'Alarm thresholds', note: 'ASHRAE 170-2021', grow: '1 1 300px',
          children: (
            <div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={thS}>{th ? 'พารามิเตอร์' : 'Parameter'}</th>
                  <th style={thS}>{th ? 'เกณฑ์' : 'Limit'}</th>
                  <th style={thS}>{th ? 'เฝ้าระวังเมื่อ' : 'Warn at'}</th>
                </tr></thead>
                <tbody>
                  {[
                    ['temp', `< ${L.tempMin + 0.5} / > ${L.tempMax - 0.5} °C`],
                    ['rh', `< ${L.rhMin + 3} / > ${L.rhMax - 3} %`],
                    ['dp', `< +${L.dpMin * 2} Pa`],
                    ['hepa', `> ${Math.round(L.hepaMax * 0.78)} Pa`],
                    ['ach', `< ${f(L.achMin * 1.1, 0)} /h`],
                  ].map(([k, w]) => (
                    <tr key={k}>
                      <td style={{ ...tdS, fontWeight: 700 }}>{th ? PMETA[k as Param].th : PMETA[k as Param].en}</td>
                      <td style={tdS}>{PMETA[k as Param].range(L)}</td>
                      <td style={{ ...tdS, color: MUT }}>{w}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 10, color: MUT, marginTop: 8, lineHeight: 1.5 }}>
                {th
                  ? 'ความดันห้องตกขณะประตูเปิดไม่นับเป็นแจ้งเตือน · เกณฑ์ HEPA ใช้ค่าความต้านทานสุดท้ายทั่วไป — ต้องยืนยันเกณฑ์กับคณะกรรมการควบคุมการติดเชื้อ (IC) และสเปกแผ่นกรองของโรงพยาบาลก่อนใช้งานจริง'
                  : 'Pressure dips while the door is open are not alarmed · HEPA limit is a typical final resistance — confirm all limits with the hospital IC committee and filter spec before go-live.'}
              </div>
            </div>
          ),
        })}
      </>)}
      {Panel({
        title: th ? 'ประวัติแจ้งเตือน · 24 ชม.' : 'Alarm log · 24 h', note: `${hist.events.length} ${th ? 'เหตุการณ์' : 'events'}`,
        children: hist.events.length === 0
          ? <div style={{ fontSize: 12, color: MUT, padding: '10px 0' }}>{th ? 'ไม่มีเหตุการณ์เกินเกณฑ์ใน 24 ชม.' : 'No excursions in the last 24 h.'}</div>
          : (
            <div style={{ overflowX: 'auto', maxHeight: 280, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
                <thead><tr>
                  <th style={thS}>{th ? 'เริ่ม' : 'Start'}</th>
                  <th style={thS}>{th ? 'ห้อง' : 'Room'}</th>
                  <th style={thS}>{th ? 'พารามิเตอร์' : 'Parameter'}</th>
                  <th style={{ ...thS, textAlign: 'right' }}>{th ? 'ค่าแย่สุด' : 'Worst'}</th>
                  <th style={{ ...thS, textAlign: 'right' }}>{th ? 'นาน' : 'Duration'}</th>
                  <th style={thS}>{th ? 'สถานะ' : 'Status'}</th>
                </tr></thead>
                <tbody>
                  {hist.events.slice(0, 50).map((e) => (
                    <tr key={`${e.ri}-${e.p}-${e.start}`}>
                      <td style={tdS}>{hhmm(e.start)}</td>
                      <td style={{ ...tdS, fontWeight: 700 }}>{rooms[e.ri].label}</td>
                      <td style={tdS}>{th ? PMETA[e.p].th : PMETA[e.p].en}</td>
                      <td style={{ ...tdS, textAlign: 'right', color: BAD, fontWeight: 700 }}>{fmtP(e.p, e.peak)} {PMETA[e.p].unit}</td>
                      <td style={{ ...tdS, textAlign: 'right' }}>{dur(((e.end ?? clock) - e.start) / 60_000)}</td>
                      <td style={tdS}>{e.end ? chip('idle', th ? 'กลับสู่ปกติ' : 'Cleared') : chip('bad', th ? 'ยังเกิดอยู่' : 'Ongoing')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ),
      })}
    </>
  );

  // ── Devices ────────────────────────────────────────────────────────────────
  const deviceTab = () => row(<>
    {Panel({
      title: th ? 'อุปกรณ์ Dixel PLC' : 'Dixel PLCs', note: 'Modbus RTU · RS-485 · 9600 8N1', grow: '1 1 100%',
      children: (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
            <thead><tr>
              <th style={thS}>{th ? 'ห้อง' : 'Room'}</th>
              <th style={thS}>Device ID</th>
              <th style={thS}>{th ? 'รุ่น' : 'Model'}</th>
              <th style={{ ...thS, textAlign: 'right' }}>Slave addr</th>
              <th style={thS}>Firmware</th>
              <th style={thS}>{th ? 'การสื่อสาร' : 'Comms'}</th>
              <th style={{ ...thS, textAlign: 'right' }}>{th ? 'อัปเดตล่าสุด' : 'Last update'}</th>
            </tr></thead>
            <tbody>
              {now.map((n) => (
                <tr key={n.r.device}>
                  <td style={{ ...tdS, fontWeight: 700 }}>{n.r.label}</td>
                  <td style={tdS}>{n.r.device}</td>
                  <td style={tdS}>{n.r.model}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{n.r.addr}</td>
                  <td style={tdS}>{n.r.fw}</td>
                  <td style={tdS}>{chip('ok', 'Online')}</td>
                  <td style={{ ...tdS, textAlign: 'right' }}>{th ? `${n.age} วินาทีก่อน` : `${n.age}s ago`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    })}
    {Panel({
      title: th ? 'Point list (ต่อ 1 PLC)' : 'Point list (per PLC)', note: th ? 'ตัวอย่าง — ยืนยันกับโปรแกรม PLC จริง' : 'placeholder — confirm against PLC program', grow: '1 1 100%',
      children: (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
            <thead><tr>
              <th style={thS}>{th ? 'รีจิสเตอร์' : 'Register'}</th>
              <th style={thS}>{th ? 'ชนิด' : 'Type'}</th>
              <th style={thS}>{th ? 'จุดวัด' : 'Point'}</th>
              <th style={thS}>{th ? 'สเกล' : 'Scale'}</th>
              <th style={thS}>{th ? 'หน่วย' : 'Unit'}</th>
              {rooms.map((r) => <th key={r.label} style={{ ...thS, textAlign: 'right' }}>{r.label}</th>)}
            </tr></thead>
            <tbody>
              {POINTS.map((p, i) => (
                <tr key={p.reg}>
                  <td style={{ ...tdS, fontFamily: 'ui-monospace, monospace' }}>{p.reg}</td>
                  <td style={tdS}>{p.type}</td>
                  <td style={{ ...tdS, fontWeight: 700 }}>{th ? p.th : p.en}</td>
                  <td style={{ ...tdS, color: MUT }}>{p.scale}</td>
                  <td style={{ ...tdS, color: MUT }}>{p.unit}</td>
                  {now.map((n) => {
                    const raw = i < PARAMS.length
                      ? Math.round(n.v[PARAMS[i].key] * (p.scale === '×0.1' ? 10 : 1))
                      : i === 5 ? (n.door ? 1 : 0) : i === 6 ? 1 : n.ahuFault ? 1 : 0;
                    return <td key={n.r.label} style={{ ...tdS, textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{raw}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    })}
  </>);

  const tabBtn = (id: Tab, label: string, badge?: number) => (
    <button key={id} onClick={() => setTab(id)}
      style={{
        padding: '6px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', borderRadius: 8, border: 'none',
        background: tab === id ? `color-mix(in srgb, ${S[0]} 14%, transparent)` : 'transparent',
        color: tab === id ? S[0] : MUT, display: 'inline-flex', alignItems: 'center', gap: 6,
      }}>
      {label}
      {!!badge && <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: BAD, borderRadius: 99, padding: '0 6px', lineHeight: '16px' }}>{badge}</span>}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', height: '100%', minHeight: 0, overflow: 'auto', fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15, fontWeight: 800, color: TXT }}>{th ? 'ติดตามห้องผ่าตัด' : 'Operating Room Monitor'}</span>
        <span title={th ? 'ข้อมูลจำลองเพื่อสาธิต — ยังไม่ได้เชื่อมกับ Dixel PLC จริง' : 'Simulated demo data — not yet connected to the Dixel PLCs'}
          style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: WARN, padding: '2px 8px', borderRadius: 6, border: `1px solid color-mix(in srgb, ${WARN} 45%, transparent)`, background: `color-mix(in srgb, ${WARN} 10%, transparent)` }}>
          DEMO
        </span>
        <span style={{ fontSize: 11, color: MUT, ...tnum }}>Dixel PLC · {hhmm(clock)}</span>
        <div style={{ display: 'flex', gap: 3, marginLeft: 'auto', flexWrap: 'wrap' }}>
          {tabBtn('overview', th ? 'ภาพรวม' : 'Overview')}
          {tabBtn('trend', th ? 'แนวโน้ม' : 'Trend')}
          {tabBtn('alarm', th ? 'แจ้งเตือน' : 'Alarms', activeBad.filter((a) => !acked[a.key]).length)}
          {tabBtn('device', th ? 'อุปกรณ์' : 'Devices')}
        </div>
      </div>
      {tab === 'overview' && overviewTab()}
      {tab === 'trend' && trendTab()}
      {tab === 'alarm' && alarmTab()}
      {tab === 'device' && deviceTab()}
    </div>
  );
}
